// Blocked Domains Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class BlockedDomains extends HTMLElement {
  constructor() {
    super();
    this.domains = [];
    this.isLoading = false;
    this.currentUser = null;
    this.isLoadingDomains = false;
    this.showAddForm = false;
    this.newDomain = '';
    this.newReason = '';
    this.actionHandler = null; // Store handler reference for cleanup
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
  }

  disconnectedCallback() {
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    // Remove action handler
    const content = this.querySelector('.domains-content');
    if (content && this.actionHandler) {
      content.removeEventListener('click', this.actionHandler);
    }
  }

  setupAuthListener() {
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;

      if (authenticated && user && user.role === 'admin') {
        this.currentUser = user;
        if (this.isVisible()) {
          this.loadDomains();
        }
      } else {
        this.currentUser = null;
      }
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
  }

  isVisible() {
    return this.offsetParent !== null && !this.style.display.includes('none');
  }

  render() {
    this.innerHTML = `
      <div class="blocked-domains">
        <div class="domains-header">
          ${renderPageTitle('🚫', 'Blocked Domains')}
          <p>Manage site-wide blocked domains (external images and widget links)</p>
        </div>

        <div class="domains-actions">
          <button class="btn btn-primary" id="addDomainBtn">
            + Add Blocked Domain
          </button>
        </div>

        ${this.renderAddForm()}

        <div class="domains-content">
          ${this.renderContent()}
        </div>
      </div>
    `;

    // Attach event listeners
    const addBtn = this.querySelector('#addDomainBtn');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        this.showAddForm = !this.showAddForm;
        this.updateAddForm();
      });
    }

    const cancelBtn = this.querySelector('#cancelAddBtn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        this.showAddForm = false;
        this.newDomain = '';
        this.newReason = '';
        this.updateAddForm();
      });
    }

    const submitBtn = this.querySelector('#submitAddBtn');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => {
        this.handleAddDomain();
      });
    }
  }

  renderAddForm() {
    return `
      <div class="add-form ${this.showAddForm ? '' : 'hidden'}" id="addForm">
        <div class="form-group">
          <label for="domainInput">Domain</label>
          <input type="text" 
                 id="domainInput" 
                 placeholder="example.com" 
                 value="${escapeHtml(this.newDomain)}"
                 required>
          <small style="color: var(--text-muted); font-size: 0.75rem; margin-top: 0.25rem; display: block;">
            Enter the domain to block (e.g., example.com). Subdomains will also be blocked.
          </small>
        </div>
        <div class="form-group">
          <label for="reasonInput">Reason</label>
          <textarea id="reasonInput" 
                    placeholder="Why is this domain being blocked?" 
                    required>${escapeHtml(this.newReason)}</textarea>
        </div>
        <div class="form-actions">
          <button class="btn btn-primary" id="submitAddBtn">Add Domain</button>
          <button class="btn btn-secondary" id="cancelAddBtn">Cancel</button>
        </div>
      </div>
    `;
  }

  updateAddForm() {
    const form = this.querySelector('#addForm');
    if (form) {
      form.classList.toggle('hidden', !this.showAddForm);
    }

    const domainInput = this.querySelector('#domainInput');
    const reasonInput = this.querySelector('#reasonInput');

    if (domainInput) {
      domainInput.value = this.newDomain;
    }
    if (reasonInput) {
      reasonInput.value = this.newReason;
    }
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading blocked domains...</div>
        </div>
      `;
    }

    if (this.domains.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🚫</div>
          <h3>No blocked domains</h3>
          <p>There are no domains currently blocked. Add a domain to get started.</p>
        </div>
      `;
    }

    return `
      <div class="domains-list">
        ${this.domains.map(domain => this.renderDomainItem(domain)).join('')}
      </div>
    `;
  }

  renderDomainItem(domain) {
    return `
      <div class="domain-item">
        <div class="domain-header">
          <div class="domain-info">
            <div class="domain-name">${escapeHtml(domain.domain)}</div>
          </div>
        </div>

        <div class="domain-reason">
          <strong>Reason:</strong> ${escapeHtml(domain.reason || 'No reason provided')}
        </div>

        <div class="domain-details">
          <div class="domain-detail">
            <span class="domain-detail-label">Blocked At</span>
            <span class="domain-detail-value">${this.formatDate(domain.created_at)}</span>
          </div>
          <div class="domain-detail">
            <span class="domain-detail-label">Blocked By</span>
            <span class="domain-detail-value">Admin (ID: ${domain.blocked_by_user_id || 'N/A'})</span>
          </div>
        </div>

        <div class="domain-actions">
          <button class="domain-action-btn delete" 
                  data-domain-id="${domain.id}" 
                  data-action="delete">
            🗑️ Delete
          </button>
        </div>
      </div>
    `;
  }

  async loadDomains() {
    if (this.isLoadingDomains) {
      return;
    }

    if (!this.currentUser || this.currentUser.role !== 'admin') {
      return;
    }

    if (!this.isVisible()) {
      return;
    }

    this.isLoadingDomains = true;
    this.isLoading = true;
    this.updateContent();

    try {
      const response = await window.API.request('/admin/blocked-domains');

      if (response && response.domains) {
        this.domains = response.domains;
      } else {
        this.domains = [];
      }
    } catch (error) {
      window.logger?.error('Failed to load blocked domains:', error);
      this.domains = [];
      const content = this.querySelector('.domains-content');
      if (content) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'error-state';
        errorDiv.textContent = 'Failed to load blocked domains. Please try again.';
        content.innerHTML = '';
        content.appendChild(errorDiv);
        return;
      }
    } finally {
      this.isLoading = false;
      this.isLoadingDomains = false;
      this.updateContent();
      // attachActionListeners is called in updateContent, no need to call it again
    }
  }

  async handleAddDomain() {
    const domainInput = this.querySelector('#domainInput');
    const reasonInput = this.querySelector('#reasonInput');

    if (!domainInput || !reasonInput) {
      return;
    }

    const domain = domainInput.value.trim();
    const reason = reasonInput.value.trim();

    if (!domain || !reason) {
      alert('Please fill in both domain and reason.');
      return;
    }

    // Basic domain validation
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(domain)) {
      alert('Please enter a valid domain name.');
      return;
    }

    const submitBtn = this.querySelector('#submitAddBtn');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding...';
    }

    try {
      await window.API.request('/admin/blocked-domains', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          domain,
          reason
        })
      });

      // Reset form and reload
      this.showAddForm = false;
      this.newDomain = '';
      this.newReason = '';
      this.updateAddForm();
      await this.loadDomains();
    } catch (error) {
      window.logger?.error('Failed to add blocked domain:', error);
      alert('Failed to add blocked domain. Please try again.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add Domain';
      }
    }
  }

  attachActionListeners() {
    const content = this.querySelector('.domains-content');
    if (!content) return;

    // Remove existing handler if any
    if (this.actionHandler) {
      content.removeEventListener('click', this.actionHandler);
    }

    // Use event delegation - attach one listener to the container
    this.actionHandler = async (e) => {
      const btn = e.target.closest('.domain-action-btn');
      if (!btn) return;

      const domainId = btn.dataset.domainId;
      const action = btn.dataset.action;

      if (!domainId || !action) return;

      if (action === 'delete') {
        if (!confirm('Are you sure you want to unblock this domain? Images from this domain will be allowed again.')) {
          return;
        }
      }

      btn.disabled = true;

      try {
        await window.API.request(`/admin/blocked-domains/${domainId}`, {
          method: 'DELETE'
        });

        // Reload domains after deletion
        await this.loadDomains();
      } catch (error) {
        window.logger?.error('Failed to delete blocked domain:', error);
        btn.disabled = false;
        alert('Failed to delete blocked domain. Please try again.');
      }
    };

    content.addEventListener('click', this.actionHandler);
  }

  updateContent() {
    const content = this.querySelector('.domains-content');
    if (content) {
      content.innerHTML = this.renderContent();
      this.attachActionListeners();
    }
  }


  formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (_e) {
      return dateString;
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    if (this.isVisible() && user && user.role === 'admin') {
      this.loadDomains();
    }
  }
}

// Register the custom element
customElements.define('blocked-domains', BlockedDomains);

